// Instanced tree trunks.
//
// Trunks are real geometry, not cards. You walk right up to a tree and orbit it,
// and a flat trunk gives the trick away instantly at close range — whereas the
// canopy is far enough overhead that cards hold up. So a tree is a trunk mesh
// plus a cluster of canopy cards, and because those cards are ordinary foliage
// instances they cost nothing extra: a whole forest is two draw calls, one for
// every trunk and one for all foliage including every canopy.
//
// The geometry is built in code rather than loaded from a GLB. A tapered
// cylinder with a few branches is a few dozen triangles; shipping it as an asset
// would add a fetch, a loader dependency and a file to keep in sync for
// something that is cheaper to just generate.

import React, { useMemo, useRef, useLayoutEffect } from 'react';
import * as THREE from 'three';

/** Trunk height at scale 1, in metres. Canopy placement depends on this. */
export const TRUNK_HEIGHT = 2.4;

export interface TrunkInstance {
  position: [number, number, number];
  scale: number;
  rotation: number;
}

/** Tapered trunk plus three angled branches, merged into one buffer. */
function trunkGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];

  // radiusTop < radiusBottom gives the taper that reads as organic; 6 radial
  // segments is enough for a stylised trunk and keeps the triangle count low
  // enough that a few hundred trees stay cheap on a headset.
  const trunk = new THREE.CylinderGeometry(0.085, 0.16, TRUNK_HEIGHT, 6, 1);
  trunk.translate(0, TRUNK_HEIGHT / 2, 0);
  parts.push(trunk);

  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 + 0.4;
    const len = 0.75 + (i % 2) * 0.25;
    const br = new THREE.CylinderGeometry(0.035, 0.07, len, 5, 1);
    br.translate(0, len / 2, 0);
    // Lean the branch outward, then swing it around the trunk.
    br.rotateZ(Math.PI * 0.28);
    br.rotateY(a);
    br.translate(0, TRUNK_HEIGHT * (0.55 + 0.12 * i), 0);
    parts.push(br);
  }

  // Manual merge — avoids pulling in BufferGeometryUtils for four cylinders.
  let vCount = 0;
  let iCount = 0;
  parts.forEach((g) => {
    vCount += g.attributes.position.count;
    iCount += g.index ? g.index.count : 0;
  });

  const pos = new Float32Array(vCount * 3);
  const nrm = new Float32Array(vCount * 3);
  const idx = new Uint16Array(iCount);
  let vo = 0;
  let io = 0;
  parts.forEach((g) => {
    const p = g.attributes.position.array as ArrayLike<number>;
    const n = g.attributes.normal.array as ArrayLike<number>;
    pos.set(p, vo * 3);
    nrm.set(n, vo * 3);
    const gi = g.index!.array as ArrayLike<number>;
    for (let k = 0; k < gi.length; k++) idx[io + k] = gi[k] + vo;
    vo += g.attributes.position.count;
    io += gi.length;
    g.dispose();
  });

  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  out.setAttribute('normal', new THREE.BufferAttribute(nrm, 3));
  out.setIndex(new THREE.BufferAttribute(idx, 1));
  out.computeBoundingSphere();
  return out;
}

export const TrunkLayer: React.FC<{ instances: TrunkInstance[]; color?: string }> = ({
  instances,
  color = '#6b4f3a',
}) => {
  const meshRef = useRef<THREE.InstancedMesh>(null);
  const geometry = useMemo(() => trunkGeometry(), []);

  useLayoutEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const up = new THREE.Vector3(0, 1, 0);
    instances.forEach((t, i) => {
      q.setFromAxisAngle(up, t.rotation);
      m.compose(
        new THREE.Vector3(...t.position),
        q,
        new THREE.Vector3(t.scale, t.scale, t.scale),
      );
      mesh.setMatrixAt(i, m);
    });
    mesh.instanceMatrix.needsUpdate = true;
    mesh.computeBoundingSphere();
  }, [instances, geometry]);

  if (!instances.length) return null;

  return (
    <instancedMesh
      ref={meshRef}
      args={[geometry, undefined, instances.length]}
      castShadow
      receiveShadow
    >
      <meshStandardMaterial color={color} roughness={0.9} metalness={0} flatShading />
    </instancedMesh>
  );
};

export default TrunkLayer;
